import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, DataTable, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { Column, DateRangeValue } from "../../../components/modern/DataTable/DataTable";
import { HiEye, HiPencil, HiTrash, HiPrinter, HiCalculator } from "react-icons/hi";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import "./ShipForMeIndex.scss";

interface ShipForMeOrder {
  id: number;
  number?: string;
  orderGroup: string;
  user: {
    id: number;
    name: string;
  };
  merchantName: string;
  description: string;
  quantity: number;
  orderStatus: number;
  servicePrice: number;
  voucherValue: number;
  weight: number;
  width: number;
  height: number;
  length: number;
  trackingId?: string;
  createdAt: string;
  updatedAt: string;
}

export const ShipForMeIndex: React.FC = () => {
  const navigate = useNavigate();
  const { token, hasPermission } = useAuth();
  const { t, i18n } = useTranslation('shipforme');
  const isRTL = i18n.language === 'ar';

  const [orders, setOrders] = useState<ShipForMeOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [dateFilters, setDateFilters] = useState<Record<string, DateRangeValue>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    const numericKeys = new Set(['id', 'quantity', 'orderStatus', 'netPrice']);

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;

      if (numericKeys.has(key)) {
        const normalized = key === 'id' ? trimmed.replace(/\D/g, '') : trimmed;
        const parsed = Number(normalized);
        if (Number.isNaN(parsed)) return;
        params.append(key, parsed.toString());
        return;
      }

      params.append(key, trimmed);
    });

    Object.entries(dateFilters).forEach(([key, range]) => {
      if (range.from) params.append(`${key}From`, range.from);
      if (range.to) params.append(`${key}To`, range.to);
    });

    return params.toString();
  };

  // Fetch orders from API
  const fetchOrders = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/adminshipforme?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('messages.fetchError'));
        return;
      }

      setOrders(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      console.error(error);
      showErrorToast(t('messages.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  // Build export query with filters - map to backend ExportFilters parameter names
  const buildExportQuery = () => {
    const params = new URLSearchParams();

    const appendInt = (key: string, raw?: string) => {
      const trimmed = raw?.trim();
      if (!trimmed) return;
      const normalized = trimmed.replace(/\D/g, '');
      if (!normalized) return;
      const parsed = Number(normalized);
      if (Number.isNaN(parsed)) return;
      params.append(key, parsed.toString());
    };

    const appendFloat = (key: string, raw?: string) => {
      const trimmed = raw?.trim();
      if (!trimmed) return;
      const normalized = trimmed.replace(/[^0-9.]/g, '');
      if (!normalized) return;
      const parsed = Number(normalized);
      if (Number.isNaN(parsed)) return;
      params.append(key, parsed.toString());
    };

    const appendText = (key: string, raw?: string) => {
      const trimmed = raw?.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    };

    appendInt('Id', filters.id);
    appendInt('OrderGroup', filters.orderGroup);
    appendText('UserName', filters.userName);
    appendText('MerchantName', filters.merchantName);
    appendInt('Quantity', filters.quantity);
    appendInt('OrderStatus', filters.orderStatus);
    appendFloat('NetPrice', filters.netPrice);

    // Date filters
    if (dateFilters.createdAt?.from) {
      params.append('StartDate', dateFilters.createdAt.from);
    }
    if (dateFilters.createdAt?.to) {
      params.append('EndDate', dateFilters.createdAt.to);
    }

    return params.toString();
  };

  // Export ship for me orders to Excel
  const handleExport = async () => {
    try {
      const query = buildExportQuery();
      const url = query ? `${import.meta.env.VITE_APP_API_BASE_URL}/adminshipforme/export?${query}` : `${import.meta.env.VITE_APP_API_BASE_URL}/adminshipforme/export`;
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export ship for me orders');
      }

      const blob = await response.blob();
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = `ShipForMe_Export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(urlObj);
      document.body.removeChild(a);
      showSuccessToast('Ship For Me orders exported successfully');
    } catch (error) {
      console.error('Error exporting ship for me orders:', error);
      showErrorToast('Failed to export ship for me orders. Please try again.');
    }
  };

  useEffect(() => {
    fetchOrders(currentPage, pageSize);
  }, [currentPage, pageSize, filters, dateFilters]);
  const getStatusBadge = (status: number) => {
    const statusColorMap: { [key: number]: string } = {
      0: "secondary", 1: "info", 2: "danger", 3: "primary", 4: "danger",
      5: "secondary", 6: "success", 7: "success", 8: "danger", 9: "warning",
      10: "warning", 11: "dark", 12: "danger", 13: "info", 14: "primary",
      15: "success", 16: "danger", 17: "warning", 18: "info", 19: "secondary",
      20: "primary", 21: "success", 22: "warning", 23: "info", 24: "primary",
      25: "success", 26: "danger", 27: "warning", 28: "danger", 29: "danger",
      30: "warning", 31: "warning"
    };

    const statusText = t(`statusOptions.${status}`, { defaultValue: "Unknown" });
    const statusColor = statusColorMap[status] || "secondary";

    return (
      <span className={`badge bg-${statusColor}`}>{statusText}</span>
    );
  };
  // Delete order
  const handleDelete = async (id: number) => {
    if (!window.confirm(t('messages.deleteConfirm'))) return;

    try {
      const response = await adminApi.call(`/adminshipforme/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || t('messages.deleteError'));
        return;
      }

      showSuccessToast(t('messages.deleteSuccess'));
      fetchOrders(currentPage, pageSize);
    } catch (error) {
      console.error(error);
      showErrorToast(t('messages.deleteError'));
    }
  };

  // Navigation handlers
  const handleCreateOrder = () => {
    navigate('/shipforme/create');
  };

  const handleViewOrder = (id: number) => {
    navigate(`/shipforme/details/${id}`);
  };

  const handleEditOrder = (id: number) => {
    navigate(`/shipforme/edit/${id}`);
  };

  const handlePrintInvoice = (id: number) => {
    navigate(`/shipforme/print/${id}`);
  };

  // Define table columns
  const columns: Column[] = [
    {
      key: 'id',
      title: t('table.orderNumber'),
      sortable: true,
      filterable: true,
      filterKey: 'id',
      width: '100px',
      render: (value, row: ShipForMeOrder) => <span className="">{row.number ?? ''}</span>
    },
    {
      key: 'orderGroup',
      title: t('table.group'),
      sortable: true,
      filterable: true,
      filterKey: 'orderGroup',
      width: '120px'
    },
    {
      key: 'user',
      title: t('table.customer'),
      filterable: true,
      filterKey: 'userName',
      filterValue: (row: ShipForMeOrder) => row.user?.name || '',
      render: (value: any, row: ShipForMeOrder) => (
        hasPermission("View User") ? (
          <Link
            to={`/customers/${row.user?.id}`}
            className="btn btn-primary btn-sm"
          >
            <HiEye className="me-1" />
            {row.user?.name}
          </Link>
        ) : (
          <span>{row.user?.name}</span>
        )
      ),
      width: '180px'
    },
    {
      key: 'merchantName',
      title: t('table.merchantName'),
      sortable: true,
      filterable: true,
      filterKey: 'merchantName',
      width: '200px'
    },
    {
      key: 'quantity',
      title: t('table.quantity'),
      sortable: true,
      filterable: true,
      filterKey: 'quantity',
      align: 'center' as const,
      width: '80px'
    },
    {
      key: 'orderStatus',
      title: t('table.status'),
      filterable: true,
      filterKey: 'orderStatus',
      filterValue: (row: ShipForMeOrder) => t(`statusOptions.${row.orderStatus}`, { defaultValue: "Unknown" }),
      render: (value: number) => getStatusBadge(value),
      width: '150px'
    },
    {
      key: 'netPrice',
      title: t('table.netPrice'),
      render: (value: any, row: ShipForMeOrder) => {
        const netPrice = (row.servicePrice - row.voucherValue).toFixed(2);
        const formattedPrice = netPrice.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return (
          <span className="fw-bold text-success">
            ${formattedPrice}
          </span>
        );
      },
      sortable: true,
      filterable: true,
      filterKey: 'netPrice',
      filterValue: (row: ShipForMeOrder) => {
        const netPrice = (row.servicePrice - row.voucherValue).toFixed(2);
        const formattedPrice = netPrice.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return `$${formattedPrice}`;
      },
      align: 'right' as const,
      width: '120px'
    },
    {
      key: 'createdAt',
      title: t('table.createdAt', 'Created At'),
      sortable: true,
      filterable: true,
      filterKey: 'createdAt',
      filterType: 'dateRange',
      width: '180px',
      render: (value) => value ? new Date(value).toLocaleString() : '-'
    },
    {
      key: 'actions',
      title: t('table.actions'),
      render: (value: any, row: ShipForMeOrder) => (
        <div className="d-flex gap-1 justify-content-center">
          {/* <ActionButton
            variant="info"
            size="sm"
            onClick={() => handleViewOrder(row.id)}
            icon={HiEye}
            tooltip={t('actions.view')}
          /> */}
          {hasPermission("Update Ship For Me") && (
            <ActionButton
              variant="warning"
              size="sm"
              onClick={() => handleEditOrder(row.id)}
              icon={HiPencil}
              tooltip={t('actions.edit')}
            />
          )}
          {/* <ActionButton
            variant="secondary"
            size="sm"
            onClick={() => handlePrintInvoice(row.id)}
            icon={HiPrinter}
            tooltip={t('actions.print')}
          /> */}
          {hasPermission("Delete Ship For Me") && (
            <ActionButton
              variant="danger"
              size="sm"
              onClick={() => handleDelete(row.id)}
              icon={HiTrash}
              tooltip={t('actions.delete')}
            />
          )}
        </div>
      ),
      width: '200px'
    }
  ];

  const breadcrumbs = [
    { label: t('breadcrumbs.shipforme') }
  ];

  return (
    <PageLayout
      title={t('title')}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={orders}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={true}
        onExport={handleExport}
        refreshable={true}
        customActions={[
          {
            label: t('actions.calculate'),
            onClick: () => navigate('/calculator/shipforme'),
            icon: HiCalculator,
            variant: 'info'
          }
        ]}
        addButton={hasPermission("Create Ship4Me Order") ? {
          label: t('addNew'),
          onClick: handleCreateOrder
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onDateRangeChange={(key, range) => {
          setDateFilters(prev => ({ ...prev, [key]: range }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchOrders(currentPage, pageSize)}
        emptyMessage={t('messages.noOrders')}
      />
    </PageLayout>
  );
};
